import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
} from '@mui/material';
import { ScopeList } from '@components/consent/ScopeList';

interface ScopeDetail {
  name: string;
  displayName: string;
  description: string;
}

interface ConsentChallengeResponse {
  requiresConsent: boolean;
  userId: string;
  clientId: string;
  clientName: string;
  clientLogoUrl?: string | null;
  redirectUri: string;
  requestedScopes: string[];
  scopeDetails: ScopeDetail[];
  language: string; // Language used by backend for scope translations
  state?: string;
  nonce?: string;
  codeChallenge?: string;
  codeChallengeMethod?: string;
}

/**
 * OAuth2 Consent Screen component.
 * Displays third-party service information and allows the user to approve or deny access.
 * Supports both scope-based permissions.
 */
export const ConsentPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [consentData, setConsentData] = useState<ConsentChallengeResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchConsentChallenge = async () => {
      try {
        // Check if early fetch was started (before React loaded)
        const earlyFetchPromise = (window as any).__CONSENT_PROMISE__;

        if (earlyFetchPromise) {
          console.debug('[ConsentPage] Using early fetch promise');

          // Wait for early fetch to complete (might already be done!)
          const data = await earlyFetchPromise;

          // Clean up promise reference
          delete (window as any).__CONSENT_PROMISE__;

          if (data) {
            // Early fetch succeeded
            if (!data.requiresConsent) {
              setError('Consent already granted. You should have been redirected.');
              setLoading(false);
              return;
            }

            setConsentData(data);

            // Synchronize frontend language with backend translations
            if (data.language && i18n.language !== data.language) {
              console.debug(`[ConsentPage] Switching language from ${i18n.language} to ${data.language}`);
              i18n.changeLanguage(data.language);
            }

            setLoading(false);
            return;
          }

          // Early fetch failed, fall through to regular fetch
          console.warn('[ConsentPage] Early fetch returned null, falling back to regular fetch');
        }

        // Fallback: Regular fetch (if early fetch didn't run or failed)
        const params = new URLSearchParams();
        searchParams.forEach((value, key) => {
          params.append(key, value);
        });

        const response = await fetch(`/connect/authorize?${params.toString()}`, {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error_description || `Failed to load consent challenge: ${response.status}`);
        }

        const data: ConsentChallengeResponse = await response.json();

        // Check if consent is actually required
        if (!data.requiresConsent) {
          // User already has an active grant - redirect should have happened on backend
          setError('Consent already granted. You should have been redirected.');
          setLoading(false);
          return;
        }

        setConsentData(data);

        // Synchronize frontend language with backend translations
        if (data.language && i18n.language !== data.language) {
          console.debug(`[ConsentPage] Switching language from ${i18n.language} to ${data.language}`);
          i18n.changeLanguage(data.language);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load consent information');
      } finally {
        setLoading(false);
      }
    };

    fetchConsentChallenge();
  }, [searchParams]);

  const handleApprove = async () => {
    console.debug('[ConsentPage] Approving consent for client:', consentData?.clientId);
    setSubmitting(true);

    try {
      // Build form data with OAuth2 parameters
      const formData = new URLSearchParams();
      formData.append('response_type', 'code');
      formData.append('client_id', consentData!.clientId);
      formData.append('redirect_uri', consentData!.redirectUri);
      formData.append('scope', consentData!.requestedScopes.join(' '));
      formData.append('approval', 'true');

      if (consentData!.state) formData.append('state', consentData!.state);
      if (consentData!.nonce) formData.append('nonce', consentData!.nonce);
      if (consentData!.codeChallenge) {
        formData.append('code_challenge', consentData!.codeChallenge);
        formData.append('code_challenge_method', consentData!.codeChallengeMethod || 'S256');
      }

      // POST to backend
      const response = await fetch('/connect/authorize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error_description || `Approval failed: ${response.status}`);
      }

      // Parse JSON response
      const data = await response.json();

      if (!data.redirectUrl) {
        throw new Error('Server did not return redirect URL');
      }

      console.debug('[ConsentPage] Redirecting to:', data.redirectUrl);

      // Perform redirect - WebView will intercept and add Bearer token
      window.location.href = data.redirectUrl;

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve consent');
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    console.debug('[ConsentPage] Denying consent for client:', consentData?.clientId);
    setSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('redirectUri', consentData!.redirectUri);
      if (consentData!.state) formData.append('state', consentData!.state);

      const response = await fetch('/connect/authorize/deny', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error_description || `Denial failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.redirectUrl) {
        throw new Error('Server did not return redirect URL');
      }

      console.debug('[ConsentPage] Redirecting to:', data.redirectUrl);
      window.location.href = data.redirectUrl;

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deny consent');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!consentData) {
    return null;
  }

  return (
    <Box display="flex" justifyContent="center" mt={4} px={2}>
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent>
          {/* Service Header */}
          <Box display="flex" alignItems="center" mb={3}>
            {consentData.clientLogoUrl && (
              <Avatar
                src={consentData.clientLogoUrl}
                alt={consentData.clientName}
                sx={{ width: 64, height: 64, mr: 2 }}
              />
            )}
            <Box>
              <Typography variant="h5" component="h1">
                {t('consent.title', 'Authorize Access')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('consent.subtitle', {
                  serviceName: consentData.clientName,
                  defaultValue: `${consentData.clientName} wants to access your Vreeda account`
                })}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Requested Permissions */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            {t('consent.permissions', 'This application can:')}
          </Typography>

          <ScopeList scopes={consentData.scopeDetails} />

          {/* Action Buttons */}
          <Box display="flex" gap={2}>
            {/* Deny Button */}
            <Button
              variant="outlined"
              color="inherit"
              fullWidth
              disabled={submitting}
              onClick={handleDeny}
              sx={{ flex: 1 }}
            >
              {t('consent.deny', 'Deny')}
            </Button>

            {/* Approve Button */}
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={submitting}
              onClick={handleApprove}
              sx={{ flex: 1 }}
            >
              {submitting ? <CircularProgress size={24} /> : t('consent.approve', 'Allow')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
